import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Wrench, X } from "lucide-react";
import {
  draftSkillsContentSchema,
  type DraftSkillsContent,
  type DraftSkillGroup,
  type DraftSkillItem,
  type SkillProficiency,
} from "@profolio/validators";

interface SkillsEditorProps {
  content: Record<string, unknown>;
  onChange: (content: DraftSkillsContent) => void;
}

export function SkillsEditor({ content, onChange }: SkillsEditorProps) {
  const [focusedGroupIndex, setFocusedGroupIndex] = useState<number | null>(null);

  const parseResult = draftSkillsContentSchema.safeParse(content);
  const data: DraftSkillsContent = parseResult.success
    ? parseResult.data
    : { heading: "Skills & Methodologies", displayStyle: "categorized_cards", groups: [] };

  const heading = data.heading || "Skills & Methodologies";
  const displayStyle = data.displayStyle || "categorized_cards";
  const groups: DraftSkillGroup[] = data.groups || [];

  const notifyChange = (
    newHeading: string,
    newStyle: "categorized_cards" | "tag_cloud" | "list",
    newGroups: DraftSkillGroup[]
  ) => {
    onChange({
      heading: newHeading,
      displayStyle: newStyle,
      groups: newGroups,
    });
  };

  const handleHeadingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    notifyChange(e.target.value, displayStyle, groups);
  };

  const addGroup = () => {
    if (groups.length >= 15) return;
    const newGroup: DraftSkillGroup = {
      id: crypto.randomUUID(),
      categoryName: "",
      skills: [],
    };
    const updated = [...groups, newGroup];
    setFocusedGroupIndex(groups.length);
    notifyChange(heading, displayStyle, updated);
  };

  const updateGroupName = (groupIndex: number, name: string) => {
    const updated = [...groups];
    updated[groupIndex] = { ...updated[groupIndex], categoryName: name };
    notifyChange(heading, displayStyle, updated);
  };

  const removeGroup = (groupIndex: number) => {
    const updated = groups.filter((_, i) => i !== groupIndex);
    notifyChange(heading, displayStyle, updated);
  };

  const addSkillToGroup = (groupIndex: number, skillName: string) => {
    if (!skillName.trim()) return;
    const updated = [...groups];
    const currentSkills = updated[groupIndex].skills || [];
    if (currentSkills.length >= 30) return;
    updated[groupIndex] = {
      ...updated[groupIndex],
      skills: [...currentSkills, { name: skillName.trim() }],
    };
    notifyChange(heading, displayStyle, updated);
  };

  const updateSkillProficiency = (
    groupIndex: number,
    skillIndex: number,
    proficiency: SkillProficiency | undefined
  ) => {
    const updated = [...groups];
    const skills = [...updated[groupIndex].skills];
    skills[skillIndex] = { ...skills[skillIndex], proficiency };
    updated[groupIndex] = { ...updated[groupIndex], skills };
    notifyChange(heading, displayStyle, updated);
  };

  const removeSkillFromGroup = (groupIndex: number, skillIndex: number) => {
    const updated = [...groups];
    const skills = updated[groupIndex].skills.filter((_: unknown, i: number) => i !== skillIndex);
    updated[groupIndex] = { ...updated[groupIndex], skills };
    notifyChange(heading, displayStyle, updated);
  };

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="skills-heading" className="text-xs font-semibold">
          Section Heading
        </Label>
        <Input
          id="skills-heading"
          value={heading}
          onChange={handleHeadingChange}
          placeholder="Skills & Methodologies"
          className="mt-1 text-xs"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-xs font-semibold">Skill Categories ({groups.length})</Label>
            <p className="text-[11px] text-muted-foreground">
              Group your proficiencies into domains (e.g. "Laboratory Methods", "Programming & Frameworks").
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addGroup}
            disabled={groups.length >= 15}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Category
          </Button>
        </div>

        {groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
            <Wrench className="mx-auto h-8 w-8 text-muted-foreground/60 mb-2" />
            <p className="text-sm font-medium">No skill categories added yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm">
              Organize your technical skills, laboratory techniques, and computational frameworks.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addGroup}
              className="mt-4 gap-1.5 text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Add First Category
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group: DraftSkillGroup, groupIndex: number) => {
              return (
                <div
                  key={group.id || groupIndex}
                  className="rounded-lg border bg-card p-4 shadow-2xs space-y-3"
                >
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Category Name (e.g. Computational & Programming)"
                      value={group.categoryName}
                      autoFocus={focusedGroupIndex === groupIndex}
                      onFocus={() => setFocusedGroupIndex(null)}
                      onChange={(e) => updateGroupName(groupIndex, e.target.value)}
                      className="font-semibold text-xs flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeGroup(groupIndex)}
                      className="h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Add Skill */}
                  <div className="flex items-center gap-2">
                    <Input
                      id={`skill-input-${groupIndex}`}
                      placeholder="Add a skill (e.g. PyTorch, Next.js, Stata) and press Enter"
                      className="text-xs flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const target = e.currentTarget;
                          addSkillToGroup(groupIndex, target.value);
                          target.value = "";
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const inputEl = document.getElementById(
                          `skill-input-${groupIndex}`
                        ) as HTMLInputElement;
                        if (inputEl) {
                          addSkillToGroup(groupIndex, inputEl.value);
                          inputEl.value = "";
                        }
                      }}
                      className="text-xs shrink-0 h-8"
                    >
                      Add Skill
                    </Button>
                  </div>

                  {/* Skill Pills */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {group.skills && group.skills.length > 0 ? (
                      group.skills.map((skill: DraftSkillItem, skillIndex: number) => (
                        <div
                          key={skillIndex}
                          className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-0.5 text-xs"
                        >
                          <span className="font-medium text-[11px]">{skill.name}</span>
                          <select
                            value={skill.proficiency || ""}
                            onChange={(e) =>
                              updateSkillProficiency(
                                groupIndex,
                                skillIndex,
                                (e.target.value as SkillProficiency) || undefined
                              )
                            }
                            className="bg-card text-[10px] text-muted-foreground border border-input/60 rounded px-1 py-0.5 focus:outline-none cursor-pointer"
                          >
                            <option value="" className="bg-card text-foreground">(Level)</option>
                            <option value="expert" className="bg-card text-foreground">Expert</option>
                            <option value="advanced" className="bg-card text-foreground">Advanced</option>
                            <option value="intermediate" className="bg-card text-foreground">Intermediate</option>
                            <option value="familiar" className="bg-card text-foreground">Familiar</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => removeSkillFromGroup(groupIndex, skillIndex)}
                            className="ml-0.5 text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-[11px] text-muted-foreground italic">
                        No skills in this category yet.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
